import React from 'react';
import {useTranslation} from 'react-i18next';
import {AgoraSubscriber, ConnectStatusIdEnum, MasterRemoteUsersTypeDef} from "../../services/AgoraService";
import {TaggedLogger} from "@nextlot/core/utilities";
import {AuctionsManager} from "@nextlot/core/webcast";


const _logger = TaggedLogger.get('BidderMediaStreamingRootCmp');


export const MEDIA_VIDEO_CONTAINER_HTML_ELEMENT_ID = 'agora_media_video_container';


export enum PlayerStatesEnum {
    connecting = 0,
    connected_ok = 3,
    playing_audio_only = 5,
    playing_video_only = 6,
    playing_audio_video = 7,
    error = -1,
}

export default function BidderMediaStreamingRootCmp() {
    _logger.debug('.render()');

    const {t} = useTranslation();

    const refAgoraSubscriber = React.useRef<AgoraSubscriber>(null);

    const [playerState, setPlayerState] = React.useState<PlayerStatesEnum>(PlayerStatesEnum.connecting);
    const [isPlayButtonPressed, setPlayButtonPressed] = React.useState(false);
    const [isAudioVolumeOn, setAudioVolumeOn] = React.useState(true);

    React.useEffect(() => {
        refAgoraSubscriber.current = new AgoraSubscriber(AuctionsManager.currentWebcastAuctionHandler);
        const agoraSubscriber = refAgoraSubscriber.current;

        agoraSubscriber.connectAndJoinChannel().then(() => {
            setPlayerState(PlayerStatesEnum.connected_ok);
        });
    }, []);



    const handleClick_PlayButton = () => {
        setPlayButtonPressed(true);

        refAgoraSubscriber.current.registerAutoSubscribeToMasterRemoteUsers((agoraSubscriber) => {


            if (agoraSubscriber.subscribedRemoteAudioTrack && !agoraSubscriber.subscribedRemoteAudioTrack.isPlaying) {
                agoraSubscriber.subscribedRemoteAudioTrack.play();
                // maintain the audio volume/mute state
                if (isAudioVolumeOn) {
                    agoraSubscriber.subscribedRemoteAudioTrack.setVolume(100);
                }
                else {
                    agoraSubscriber.subscribedRemoteAudioTrack.setVolume(0);
                }
            }

            if (agoraSubscriber.subscribedRemoteVideoTrack && !agoraSubscriber.subscribedRemoteVideoTrack.isPlaying) {
                agoraSubscriber.subscribedRemoteVideoTrack.play(MEDIA_VIDEO_CONTAINER_HTML_ELEMENT_ID, {fit: "contain"});
            }

            if (agoraSubscriber.subscribedRemoteAudioTrack && agoraSubscriber.subscribedRemoteVideoTrack) {
                setPlayerState(PlayerStatesEnum.playing_audio_video);
            }
            else if (agoraSubscriber.subscribedRemoteAudioTrack) {
                setPlayerState(PlayerStatesEnum.playing_audio_only);
            }
            else if (agoraSubscriber.subscribedRemoteVideoTrack) {
                setPlayerState(PlayerStatesEnum.playing_video_only);
            }
            else {
                // there are no subscribed tracks
                setPlayerState(PlayerStatesEnum.connected_ok);
            }
        });
    };

    const handleChange_checkboxAudioVolumeOn = (evt) => {
        const isAudioOnChecked = evt.currentTarget.checked;
        const remoteAudioTrack = refAgoraSubscriber.current.subscribedRemoteAudioTrack;
        if (remoteAudioTrack?.isPlaying) {
            if (isAudioOnChecked) {
                remoteAudioTrack.setVolume(100);
            }
            else {
                remoteAudioTrack.setVolume(0);
            }
        }

        setAudioVolumeOn(isAudioOnChecked);
    };



    let playTracks:{ hasAudio?: boolean, hasVideo?: boolean } = null;
    let playTracksLabel:string = null;
    switch (playerState) {
        case PlayerStatesEnum.playing_audio_only: {
            playTracks = { hasAudio: true };
            playTracksLabel = 'Audio only';
            break;
        }
        case PlayerStatesEnum.playing_video_only: {
            playTracks = { hasVideo: true };
            playTracksLabel = 'Video only';
            break;
        }
        case PlayerStatesEnum.playing_audio_video: {
            playTracks = { hasAudio: true, hasVideo: true };
            playTracksLabel = 'Audio + Video';
            break;
        }
    }


    return (
        <div className="bidder-media-streaming-outline">
            <div className="bidder-cmp bidder-cmp--media-streaming">
                <div className='cmp__body'>
                    {
                        playerState === PlayerStatesEnum.error
                            ? <div className="p-2">
                                <h5 className="text-danger">{t('mediaStreaming.failedConnection')}</h5>
                            </div>
                            : (playerState >= PlayerStatesEnum.connected_ok)
                                ?  <>
                                    <div className="media-video-container" id={MEDIA_VIDEO_CONTAINER_HTML_ELEMENT_ID} />
                                    {
                                        (isPlayButtonPressed)
                                            ? (
                                                (!playTracks)
                                                &&
                                                <div className="media-controls-container">
                                                    <div className='p-2 text-light text-bg-dark'>Live stream pending ... please wait</div>
                                                </div>
                                            )
                                            : <div className="media-controls-container">
                                                <button type="button" className='btn btn-lg btn-success' onClick={handleClick_PlayButton}>{t('mediaStreaming.playBtn')}</button>
                                            </div>
                                    }
                                </>
                                : <div className='p-2'>
                                    <span><i className="fa-regular fa-signal-stream text-body-secondary" /> &nbsp; {t('mediaStreaming.connecting')}</span>
                                </div>

                    }
                </div>

                {
                    (playerState >= PlayerStatesEnum.connected_ok)
                    &&
                    <div className="cmp__footer">
                        <div>
                            <i className="fa-regular fa-signal-stream text-success"/>
                            &nbsp; {playerState > PlayerStatesEnum.connected_ok ? `${t('mediaStreaming.playing_live', 'Live')} (${playTracksLabel})` : t('mediaStreaming.connected', 'Connected')}
                        </div>

                        {
                            (playerState === PlayerStatesEnum.playing_audio_video || playerState === PlayerStatesEnum.playing_audio_only)
                            &&
                            <label>
                                {
                                    isAudioVolumeOn
                                        ? <i className='fa-regular fa-volume-high fa-fw' />
                                        : <i className='fa-regular fa-volume-slash fa-fw' />
                                }

                                &nbsp;

                                <input type='checkbox' value='volume_on'
                                       className='ms-1 form-check-input'
                                       checked={isAudioVolumeOn}
                                       onChange={handleChange_checkboxAudioVolumeOn}
                                />
                            </label>
                        }
                    </div>
                }
            </div>
        </div>
    );
}
